import { CommonModule } from '@angular/common'
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder
} from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router'
import { InitiativeCategory } from '@common/enums/policy-initiative/initiative-category.enum'
import { CaseInputComponent, HasPermissionDirective } from 'src/app/abacus'
import { InfoBoxComponent } from 'src/app/shared/components/info-box/info-box.component'
import { CaseCreateEditComponent } from '../../../abacus/components/case-create-edit.component'
import { ResourceMode } from '../../../abacus/enums/resource-mode.enum'
import { Field } from '../../../abacus/interfaces/field.interface'
import { ResourceDefinition } from '../../../abacus/interfaces/resource-definition.interface'
import { BreadcrumbService } from '../../../abacus/services/breadcrumb.service'
import { FlashMessageService } from '../../../abacus/services/flash-message.service'
import { ResourceService } from '../../../abacus/services/resource.service'
import { StepProgressBarComponent } from '../../../shared/components/step-progress-bar/step-progress-bar.component'
import {
  policyInitiativeDefinition,
  policyInitiativeSteps
} from '../policy-initiative.definition'
import { getStep1Fields } from './fields/step-1-fields'
import { setStep1ConditionalForm } from './fields/step-1-functions'
import { getStep2Fields } from './fields/step-2-fields'
import { setStep2ConditionalForm } from './fields/step-2-functions'
import { step3Fields } from './fields/step-3-fields'
import { IntroductionComponent } from './introduction.component'

@Component({
  templateUrl: './policy-initiative-create-edit.component.html',
  styleUrls: ['./policy-initiative-create-edit.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HasPermissionDirective,
    RouterModule,
    StepProgressBarComponent,
    IntroductionComponent,
    InfoBoxComponent,
    CaseInputComponent,
    FormsModule
  ]
})
export class PolicyInitiativeCreateEditComponent
  extends CaseCreateEditComponent
  implements OnInit
{
  protected readonly policyInitiativeSteps = policyInitiativeSteps
  destroyRef = inject(DestroyRef)
  cdr = inject(ChangeDetectorRef)
  definition: ResourceDefinition = policyInitiativeDefinition
  loading = false
  currentStep: number = 0
  InitiativeCategory = InitiativeCategory
  isInitiativeTypeFieldError = signal<boolean>(false)

  constructor(
    formBuilder: UntypedFormBuilder,
    router: Router,
    activatedRoute: ActivatedRoute,
    resourceService: ResourceService,
    breadcrumbService: BreadcrumbService,
    flashMessageService: FlashMessageService,
    private componentActivatedRoute: ActivatedRoute,
    private componentRouter: Router,
    private componentFlashMessageService: FlashMessageService,
    public componentResourceService: ResourceService,
    private sanitizer: DomSanitizer
  ) {
    super(
      formBuilder,
      router,
      breadcrumbService,
      resourceService,
      flashMessageService,
      activatedRoute
    )
  }

  ngOnInit() {
    this.componentActivatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (params: Params) => {
        this.showErrors = false
        this.currentStep = params.step ? parseInt(params.step, 10) : 0
        this.mode =
          this.componentActivatedRoute?.snapshot?.data?.mode ||
          ResourceMode.Create

        if (this.mode === ResourceMode.Create && this.currentStep > 1) {
          this.componentFlashMessageService.error('Error: Invalid step.')
          this.componentRouter.navigate([], {
            queryParams: { step: null },
            queryParamsHandling: 'merge'
          })
        }
        if (this.mode === ResourceMode.Edit && !this.currentStep) {
          this.currentStep = 1
        }

        if (this.mode === ResourceMode.Edit) {
          this.item = await this.componentResourceService
            .show(
              this.definition.slug,
              this.componentActivatedRoute?.snapshot?.params?.id
            )
            .then((itemRes) => itemRes)
            .catch(() => {
              this.componentFlashMessageService.error(
                'Error: Policy initiative not found.'
              )
              this.componentRouter.navigate(['/404'])
            })
        }

        switch (this.currentStep) {
          case 0:
            this.fields = []
            break
          case 1:
            this.fields = getStep1Fields(this)
            break
          case 2:
            this.fields = getStep2Fields(this)
            break
          case 3:
            this.fields = step3Fields
            break
        }

        this.resolvedFields = await this.resolveFields(this.fields)
        this.form = await this.generateForm(this.resolvedFields)

        this.setBreadcrumbs()

        // Set the form conditional fields based on the existing values.
        if (this.mode === ResourceMode.Edit) {
          if (this.currentStep === 1) {
            await setStep1ConditionalForm(this, this.item)
          } else if (this.currentStep === 2) {
            await setStep2ConditionalForm(this, this.item)
          }
        }
      })
  }

  /**
   * Save the form as draft and go to a defined step. Validate the fields of the current step before proceeding.
   * Once the draft is saved, we will force an EDIT mode to retrieve other step's fields.
   *
   * @param step the step to go to.
   * @param skipPristine if true, the pristine fields will be skipped (useful for going back to a previous step).
   *
   * @returns void
   */
  async saveAndGoToStep({
    step,
    skipPristineValidation
  }: {
    step: number
    skipPristineValidation?: boolean
  }): Promise<void> {
    const stepInvalidFields: Field[] = this.getInvalidFields({
      fields: this.fields,
      form: this.form,
      skipPristineValidation
    })

    if (stepInvalidFields.length) {
      this.showErrors = true

      this.componentFlashMessageService.error(
        'Some fields are invalid. Please correct them before proceeding: ' +
          stepInvalidFields
            .map((field: Field) => {
              if (field.id === 'initiativeType') {
                this.isInitiativeTypeFieldError.set(true)
                return field.label
              }
              return field.label
            })
            .join(', ')
      )

      // Scroll to top.
      const firstErrorField = document.querySelector('h1')
      if (firstErrorField) {
        // Smooth scroll to the element
        firstErrorField.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }

      return
    }

    this.item = await this.save()

    this.componentRouter.navigate(
      ['/policy-initiatives', this.item.id, 'edit'],
      {
        queryParams: { step },
        queryParamsHandling: 'merge'
      }
    )
  }

  goToStep(step: number): void {
    if (step === 0) {
      this.componentRouter.navigate(['/policy-initiatives', 'create'])
      return
    }

    this.componentRouter.navigate(
      this.item
        ? ['/policy-initiatives', this.item.id, 'edit']
        : ['/policy-initiatives', 'create'],
      {
        queryParams: { step },
        queryParamsHandling: 'merge'
      }
    )
  }

  /**
   * Override onValueChanged to reset error signal when initiativeTypeId is changed and becomes valid
   */
  // should be re-written with new flow and not be based on field.property , just to have a single form
  // super is not good decision which is implemented for case-input component
  override onValueChanged(newValue: any, field: Field): void {
    super.onValueChanged(newValue, field)

    if (field.property === 'initiativeTypeId') {
      const control = this.form.get('initiativeTypeId')
      if (control && control.valid) {
        this.isInitiativeTypeFieldError.set(false)
      }
    }
  }

  /**
   * Check if the fields are valid for a given set of fields.
   *
   * @param fields the fields to check.
   * @param form the form to check the fields against.
   * @param skipPristineValidation if true, the pristine fields will be skipped (useful for going back to a previous step).
   *
   * @returns the invalid fields.
   */
  getInvalidFields({
    fields,
    form,
    skipPristineValidation
  }: {
    fields: Field[]
    form: FormGroup
    skipPristineValidation?: boolean
  }): Field[] {
    if (skipPristineValidation) {
      return fields.filter(
        (field: Field) =>
          form.get(field.property).invalid && !form.get(field.property).pristine
      )
    }

    return fields.filter((field: Field) => {
      if (field.hidden) {
        return false
      }

      const fieldProperties: string[] = field.property
        ? [field.property]
        : Object.keys(field.properties)

      const isInvalid = fieldProperties.some((property: string) => {
        const control = form.get(property)
        if (
          this.mode === ResourceMode.Edit &&
          control &&
          (control.value === null || control.value === '')
        ) {
          if (field.forcedValue !== undefined && field.forcedValue !== null) {
            const valueToSet = field.forcedValue
            control.setValue(valueToSet, { emitEvent: false })
            control.updateValueAndValidity()
          } else if (this.item) {
            const itemVal = (this.item as any)[property]
            if (itemVal !== undefined && itemVal !== null) {
              const valueToSet =
                property === 'initiativeTypeId' && typeof itemVal === 'number'
                  ? String(itemVal)
                  : itemVal
              control.setValue(valueToSet, { emitEvent: false })
              control.updateValueAndValidity()
              field.forcedValue = valueToSet
            }
          }
        }

        const invalid = control?.invalid

        return invalid
      })

      return isInvalid
    })
  }

  async save(): Promise<{ id: number }> {
    this.loading = true

    if (this.mode === ResourceMode.Create) {
      return this.componentResourceService
        .store(this.definition.slug, this.form.value)
        .then((res) => {
          this.loading = false
          this.componentFlashMessageService.success(
            'Policy initiative has been saved'
          )
          return res
        })
        .catch((error) => {
          this.loading = false
          this.componentFlashMessageService.error(
            'Failed to save policy initiative'
          )
        })
    } else {
      return this.componentResourceService
        .patch(
          `${this.definition.slug}/${this.item.id}/step-${this.currentStep}`,
          this.form.value
        )
        .then((res) => {
          this.loading = false
          this.componentFlashMessageService.success(
            'Policy initiative has been saved'
          )
          return this.item
        })
        .catch((error) => {
          this.loading = false
          this.componentFlashMessageService.error(
            'Failed to save policy initiative'
          )
        })
    }
  }

  async submit() {
    this.loading = true
    // Patch current data
    await this.componentResourceService.patch(
      `${this.definition.slug}/${this.item.id}/step-${this.currentStep}`,
      this.form.value
    )

    await this.componentResourceService
      .patch(`${this.definition.slug}/${this.item.id}/submit`)
      .then(() => {
        this.loading = false
        this.componentFlashMessageService.success(
          'The policy initiative has been submitted'
        )

        this.componentRouter.navigate(['/policy-initiatives'])
      })
      .catch(() => {
        this.loading = false
        this.componentFlashMessageService.error(
          'Failed to submit policy initiative'
        )
      })
  }

  public infoBoxContentNationalStrategy: SafeHtml = this.sanitizer
    .bypassSecurityTrustHtml(`
  <p>
    A national AI strategy can be defined as an official document laying out a given country's vision for AI policy and acting as main reference for its implementation.
    It typically includes elements such as the rationale behind such policy, purported goals and criteria to evaluate performance/success, main areas of focus and policy actions,
    key sectors covered (if any), key actors and their responsibilities (including in terms of governance and oversight), and resourcing.
  </p>

  <br>

  <p><strong>As a general rule</strong>, broader strategies or strategies in connected fields (data, quantum, digital skills…) should <strong>not</strong> be in this category.</p>

    <br>

  <p><strong>Exception:</strong> Digital strategies can be included in this category if a given country has chosen not to adopt an AI strategy and use their digital strategy as main vehicle to steer AI policy instead.</p>
`)
}
